import { useEffect, useState } from 'react';
import { Brain, LightBulb, Trash } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Skeleton } from 'components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import { useMemories, useDeleteMemory } from 'features/memory/hooks/use-memory';
import MemoryDeleteAlertDialog from 'features/memory/components/memory-alert-dialog';
import { useToast } from 'hooks/use-toast';

interface MemoryDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

type Memory = { id: string; text: string };

/** Saved memories list with delete, in the shared dialog style (title, description, bordered list). */
export const MemoryDialog = ({ isOpen, setIsOpen }: MemoryDialogProps) => {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useMemories();
  const { mutate: deleteMemory, isPending: isDeleting } = useDeleteMemory();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memoryToDelete, setMemoryToDelete] = useState<Memory | null>(null);
  const { toast } = useToast();

  const memories: Memory[] = data?.memories ?? [];

  useEffect(() => {
    if (isOpen) refetch();
  }, [isOpen, refetch]);

  const handleDeleteClick = (memory: Memory) => {
    setMemoryToDelete(memory);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!memoryToDelete) return;
    deleteMemory(memoryToDelete.id, {
      onSuccess: () => {
        toast({
          variant: 'success',
          title: t('SUCCESS', 'Success'),
          description: t('MEMORY_DELETED_SUCCESSFULLY'),
        });
        setDeleteDialogOpen(false);
        setMemoryToDelete(null);
      },
    });
  };

  let content = (
    <div className="flex h-full flex-col items-center justify-center px-4 py-12 text-center">
      <div className="mb-5 flex size-14 items-center justify-center rounded-full bg-grey-100 text-grey-500 dark:bg-muted dark:text-muted-foreground">
        <Brain width={28} height={28} strokeWidth={1.5} />
      </div>
      <h3 className="mb-2 text-base font-medium leading-heading text-grey-950 dark:text-foreground">
        {t('NO_MEMORIES_YET', { defaultValue: 'No Memories Yet' })}
      </h3>
      <p className="max-w-md text-sm leading-body text-grey-500 dark:text-muted-foreground">
        {t('NO_MEMORIES_YET_DESCRIPTION', {
          defaultValue:
            "You haven't saved any memories yet. Start a conversation to create your first memory that will be remembered forever.",
        })}
      </p>
      <p className="mt-5 flex items-center gap-1.5 rounded-card border border-dashed border-grey-200 bg-grey-50 px-3 py-2 text-xs leading-heading text-grey-600 dark:border-border dark:bg-muted dark:text-muted-foreground">
        <LightBulb width={14} height={14} strokeWidth={1.5} className="shrink-0" />
        <span>
          <strong className="font-medium">{t('MEMORY_TIP', { defaultValue: 'Tip:' })}</strong>{' '}
          {t('MEMORIES_HELP', {
            defaultValue: 'Memories help remember important information across conversations',
          })}
        </span>
      </p>
    </div>
  );

  if (isLoading) {
    content = (
      <div className="flex flex-col gap-2 p-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-8 w-full" />
        ))}
      </div>
    );
  } else if (memories.length > 0) {
    content = (
      <ul className="divide-y divide-grey-100 dark:divide-border">
        {memories.map((memory, index) => (
          <li
            key={memory.id || index}
            className="flex items-center justify-between gap-3 px-4 py-2"
          >
            <span className="min-w-0 break-words text-sm text-grey-950 dark:text-foreground">
              {memory.text}
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={t('DELETE', { defaultValue: 'Delete' })}
                  onClick={() => handleDeleteClick(memory)}
                  disabled={isDeleting}
                  className="flex size-9 shrink-0 items-center justify-center rounded-card text-grey-600 hover:bg-grey-50 disabled:opacity-50 dark:text-muted-foreground dark:hover:bg-muted"
                >
                  <Trash width={16} height={16} strokeWidth={1.5} />
                </button>
              </TooltipTrigger>
              <TooltipContent>{t('DELETE', { defaultValue: 'Delete' })}</TooltipContent>
            </Tooltip>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent nested className="flex h-[min(36rem,85vh)] flex-col sm:max-w-2xl">
        <DialogHeader className="space-y-1">
          <DialogTitle className="text-lg font-medium tracking-normal text-grey-950 dark:text-foreground">
            {t('SAVED_MEMORIES', { defaultValue: 'Saved Memories' })}
          </DialogTitle>
          <DialogDescription className="text-sm font-normal text-grey-500 dark:text-muted-foreground">
            {t('SAVED_MEMORIES_DESCRIPTION', {
              defaultValue:
                'Buddy tries to remember your recent chats, but it may forget things over time. Saved memories are never forgotten.',
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto rounded-section border border-grey-100 dark:border-border">
          {content}
        </div>
      </DialogContent>
      <MemoryDeleteAlertDialog
        deleteDialogOpen={deleteDialogOpen}
        setDeleteDialogOpen={setDeleteDialogOpen}
        memoryToDelete={memoryToDelete}
        handleConfirmDelete={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </Dialog>
  );
};
